import {Box, LinearProgress, Typography} from '@mui/material';

import {
  BenchmarkDeltaVariant,
  DEFAULT_SCORE_STAT_THRESHOLDS,
  getScoreStatColors,
  ScoreStatThresholds,
} from './scoreStatUtils';
import {scorePill} from '../../layouts/theme/designSystem';

export interface Props {
  label: string;
  score: number | null;
  deltaLabel?: string | null;
  normLabel?: string | null;
  deltaVariant?: BenchmarkDeltaVariant | null;
  fillWidth?: boolean;
  thresholds?: ScoreStatThresholds;
}

const deltaChipStyles: Record<BenchmarkDeltaVariant, {bgcolor: string; color: string}> = {
  up: {bgcolor: scorePill.high.bgcolor, color: scorePill.high.color},
  down: {bgcolor: scorePill.low.bgcolor, color: scorePill.low.color},
  flat: {bgcolor: 'action.hover', color: 'text.secondary'},
};

export const ScoreStatCard = ({
  label,
  score,
  deltaLabel,
  normLabel,
  deltaVariant,
  fillWidth = true,
  thresholds = DEFAULT_SCORE_STAT_THRESHOLDS,
}: Props) => {
  const scoreColors = score !== null ? getScoreStatColors(score, thresholds) : null;

  return (
    <Box
      sx={{
        ...(fillWidth ? {width: '100%', minWidth: 0, boxSizing: 'border-box'} : {}),
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        bgcolor: 'background.paper',
        px: 1.75,
        py: 1.5,
      }}
    >
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          fontSize: '10.5px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: 'text.secondary',
          mb: 0.625,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: '28px',
          fontWeight: 600,
          letterSpacing: '-1px',
          lineHeight: 1,
          mb: 0.875,
          color: scoreColors?.color ?? 'text.secondary',
        }}
      >
        {score !== null ? `${score}%` : '—'}
      </Typography>

      <LinearProgress
        variant='determinate'
        value={score ?? 0}
        sx={{
          height: 3,
          borderRadius: '99px',
          bgcolor: 'action.hover',
          mb: 0.875,
          '& .MuiLinearProgress-bar': {
            borderRadius: '99px',
            bgcolor: scoreColors?.barColor ?? 'transparent',
          },
        }}
      />

      {(deltaLabel || normLabel) && (
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
          {deltaLabel && deltaVariant ? (
            <Box
              component='span'
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                px: 0.75,
                py: 0.25,
                borderRadius: '99px',
                ...deltaChipStyles[deltaVariant],
              }}
            >
              {deltaLabel}
            </Box>
          ) : (
            <Box />
          )}
          {normLabel && (
            <Typography variant='caption' sx={{color: 'text.secondary', fontSize: '11px', whiteSpace: 'nowrap'}}>
              {normLabel}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
